import React, { useContext, useMemo, useState } from 'react'
import { Divider, Container } from '@material-ui/core'
import { Action } from '../../models/Action'
import { ACTIONS } from '../../context/PolicyManagementContext'
import { awaitWithLoading } from '../../services/utils'
import { SkywatchDialog } from '../PopUps/SkywatchDialog'
import { AdditionalInsuredForm } from '../AdditionalInsured/AdditionalInsuredForm'
import { ReactComponent as PersonCircle } from '../../assets/person_with_circle.svg'
import policymanagementApi from '../../api/policymanagement.api'
import { EventEmitterService, EventKey } from '../../services/EventEmitterService'
import './PolicyListCard.scss'
import DataLayerService from '../../services/DataLayerService'
import { FirebaseService, FirebaseConfigKey } from '../../services/FirebaseService'
import { isMobile } from 'react-device-detect'
import { PolicyManagementContextStore } from '../../context/PolicyManagementContext'
import { CoverageDetails } from './PolicyView/PolicyDetailSections/CoverageDetails'
import { ListPolicyCOIView } from './PolicyView/PolicyCOIDetails/ListPolicyCOIView'
import PolicyCOIExtensionContext from '../../context/PolicyCOIExtensionContext'
import { AdminActions } from './PolicyView/PolicyDetailSections/AdminActions'
import { ManageAdditionalInsureds } from './PolicyView/PolicyDetailSections/ManageAdditionalInsureds'
import { AdditionalInsuredsCertificates } from './PolicyView/PolicyDetailSections/AdditionalInsuredsCertificates'
import { AutoRenewalConfirmationDialog } from '../PopUps/AutoRenewalConfirmationDialog'
import PolicyCertificates from './PolicyCertificates'
import PolicyDetailsGrid from './PolicyDetailsGrid'
import PolicyHeader from './PolicyHeader'
import { SessionService } from '../../services/SessionService'
import FlexView from 'react-flexview/lib'
import { InsuranceStatus } from '../../models/insurance-models/InsuranceStatus'
import EditIcon from '@material-ui/icons/Edit';
import { ModifyPolicyDialog } from './PolicyView/ModifyPolicy/ModifyPolicyDialog'

export const PolicyListCard = ({insurance, small }) => {
    const [isRenewal, setIsRenewal] = useState(false)
    const [isCoverageDetailsExpanded, setIsCoverageDetailsExpanded] = useState(false)
    const [openModify, setOpenModify] = useState(false)
    const [showAddAdditionalInsuredCertificatePopup, setShowAddAdditionalInsuredCertificatePopup] = useState(false)
    const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
    const [showAddAdditionalInsuredPopup, setShowAddAdditionalInsuredPopup] = useState(false)
    const isActiveInsurance = useMemo(()=> insurance.getStatus() == InsuranceStatus.SCHEDULED || insurance.getStatus() == InsuranceStatus.ACTIVE,[])

    const policyManagementContext = useContext(PolicyManagementContextStore)

    const dateFormat = isMobile || small 
        ? FirebaseService.getValue(FirebaseConfigKey.SHORT_DATE_FORMAT).toUpperCase() 
        : FirebaseService.getValue(FirebaseConfigKey.LONG_DATE_FORMAT)

    const openAddAdditionalInsured = () => {
        setShowAddAdditionalInsuredPopup(true)
        DataLayerService.pushToDataLayer({
            'event': 'additional-insured-open', 
            at: window.location.pathname
        })
    }

    const openEditCoverageDetailsDialog = () => {
        setOpenModify(true)
    }

    const closeEditCoverageDetailsDialog = () => {
        setOpenModify(false)
    }

    const openConfirmationDialog = () => {
        if (insurance.autoRenew) {
            setConfirmDialogOpen(true)
        } else {
            toggleAutoRenew()
        }
    }

    const openPolicyUrl = () => {
        window.open(insurance.policyUrl, '_blank')
    }

    const openCOIUrl = () => {
        window.open(insurance.certificateUrl, '_blank')
    }

    const toggleAutoRenew = async () => {
        const promise = insurance.autoRenew 
            ? policymanagementApi.disableAutoRenew(insurance.insuranceId) 
            : policymanagementApi.enableAutoRenew(insurance.insuranceId)
        
        const res = await awaitWithLoading(promise)
        setConfirmDialogOpen(false)
        
        if (res.ok) {
            if (insurance.autoRenew) {
                EventEmitterService.dispatch('OPEN_AUTO_RENEW_WINDOW')
            }
            policyManagementContext.dispatch(new Action(ACTIONS.TOGGLE_AUTO_RENEW, {
                insuranceId: insurance.insuranceId
            }))
        } else {
            EventEmitterService.dispatch(EventKey.ShowError, res)
        }
    }
    return (
        <div className="policy-card">
            <PolicyHeader 
                isActiveInsurance={isActiveInsurance}
                isRenewal={isRenewal}
                setIsRenewal={setIsRenewal}
                setIsCoverageDetailsExpanded={setIsCoverageDetailsExpanded}
                insurance={insurance}
                small={small}
            />

            <PolicyDetailsGrid 
                small={small}
                isActiveInsurance={isActiveInsurance}
                isRenewal={isRenewal}
                insurance={insurance}
                dateFormat={dateFormat}
                openConfirmationDialog={openConfirmationDialog}
            />

            {!isRenewal && (
                <PolicyCertificates 
                    shrink={small}
                    isMobile={isMobile}
                    openCOIUrl={openCOIUrl}
                    openPolicyUrl={openPolicyUrl}
                />
            )}

            {isActiveInsurance && (!isRenewal || insurance.autoRenew) && <button className="button-outline full-width margin-bottom-large" onClick={openEditCoverageDetailsDialog}>

                <FlexView column={false} className={`${isMobile && 'text-medium'} bold`} vAlignContent="center" hAlignContent="center">
                    <EditIcon fontSize="small" className="margin-right-small" />
                    Update Coverage Details
                </FlexView>
            </button>}

            <Container className="no-padding">
                {!isRenewal && (
                    <>
                        {isActiveInsurance && <ManageAdditionalInsureds 
                            shrink={small}
                            openAddAdditionalInsured={openAddAdditionalInsured} 
                        />}
                         
                            <AdditionalInsuredsCertificates 
                                shrink={small}
                                setShowAddAdditionalInsuredCertificatePopup={setShowAddAdditionalInsuredCertificatePopup}
                            />
                        
                    </>
                )} 

                <CoverageDetails 
                    shrink={small}
                    isExpanded={isCoverageDetailsExpanded} 
                    setIsExpanded={setIsCoverageDetailsExpanded} 
                    insurance={insurance} 
                    isRenewal={isRenewal} 
                />
                
                {SessionService.isAdmin() && (
                    <>
                        <Divider />
                        <AdminActions shrink={small} insurance={insurance} />
                    </>
                )}
            </Container>

            <AutoRenewalConfirmationDialog
                open={confirmDialogOpen}
                onClose={() => setConfirmDialogOpen(false)}
                onConfirm={toggleAutoRenew}
                insurance={insurance}
            />

            <SkywatchDialog 
                open={showAddAdditionalInsuredPopup}
                closeDialog={() => setShowAddAdditionalInsuredPopup(false)}
                maxWidth={isMobile ? 'md' : 'xs'}
                circleHeader={<PersonCircle />}
                closeTitle="Add Additional Insured"
            >

                <AdditionalInsuredForm 
                    insurance={insurance} 
                    closeDialog={() => setShowAddAdditionalInsuredPopup(false)}
                    />
            </SkywatchDialog>

            <SkywatchDialog 
                open={showAddAdditionalInsuredCertificatePopup}
                closeDialog={() => setShowAddAdditionalInsuredCertificatePopup(false)}
                maxWidth={isMobile ? 'md' : 'xs'}
                circleHeader={<PersonCircle />}
                closeTitle="Select and download additional insured certification"
            >
                <PolicyCOIExtensionContext>
                    <ListPolicyCOIView insurance={insurance}/>
                </PolicyCOIExtensionContext>
            </SkywatchDialog>

            <ModifyPolicyDialog 
                insurance={insurance} 
                isRenewal={isRenewal} 
                openModify={openModify} 
                closeEditCoverageDetailsDialog={closeEditCoverageDetailsDialog}
            />

        </div>

    )
}